import {
    XAxis, YAxis, CartesianGrid,
    Tooltip as RechartTooltip, ResponsiveContainer,
    LineChart, Line, Legend,
} from 'recharts';
import type { TooltipContentProps } from 'recharts/types/component/Tooltip';
import type { DailyPoint, DayRange } from '@/types/meta-lead';
import { MousePointerClick, TrendingUp, CalendarDays } from 'lucide-react';

interface TopLead { id: number; nome: string; sobrenome?: string; page_id: string; total_clicks: number }

interface LeadStatsProps {
    totalLeads?: number;
    activeLeads?: number;
    leadsInFunnel?: number;
    leadsNewToday?: number;
    leadsInteractedToday?: number;
    leadsIn24h?: number;
    totalClicks?: number;
    clicksToday?: number;
    clicksThisWeek?: number;
    clicksThisMonth?: number;
    clicksFunnel?: number;
    clicksBroadcast?: number;
    uniqueLeadsClicked?: number;
    topLeads?: TopLead[];
    totalPages?: number;
    activePages?: number;
    leadsReached?: number;
    leadsWebhook?: number;
    leadsSync?: number;
    isUpdating?: boolean;
    days?: DayRange;
    onDaysChange?: (d: DayRange) => void;
    daily?: DailyPoint[];
}

const fmt = (n = 0) => n.toLocaleString('pt-BR');

const AVATAR_COLORS = ['bg-blue-500', 'bg-violet-500', 'bg-emerald-500', 'bg-orange-400', 'bg-pink-500', 'bg-cyan-500', 'bg-amber-500', 'bg-rose-500'];

function MetricCard({ label, value, sub, icon }: { label: string; value: string; sub?: string; icon: React.ReactNode }) {
    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-start justify-between mb-2">
                <span className="text-xs text-gray-400">{label}</span>
                <span className="text-gray-300">{icon}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
        </div>
    );
}

function renderDailyTooltip(props: TooltipContentProps<number, string>) {
    if (!props.active || !props.payload?.length) return null;
    return (
        <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-md text-xs">
            <p className="text-gray-400 mb-1">{props.label}</p>
            {props.payload.map(p => (
                <p key={String(p.dataKey)} style={{ color: p.color }} className="font-semibold">
                    {p.name}: {fmt(p.value as number)}
                </p>
            ))}
        </div>
    );
}

function KpiCard({ label, value, badge, badgeClass = 'text-gray-500' }: { label: string; value: string; badge: string; badgeClass?: string }) {
    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col gap-1">
            <p className="text-xs text-gray-400">{label}</p>
            <p className="text-3xl font-bold text-gray-900">{value}</p>
            <p className={`text-[11px] ${badgeClass}`}>{badge}</p>
        </div>
    );
}

function IndicatorRow({ label, value, total, color = 'bg-blue-500' }: { label: string; value: number; total?: number; color?: string }) {
    const pct = total && total > 0 ? Math.round((value / total) * 100) : 0;
    return (
        <div className="py-2.5 border-t border-gray-100 first:border-0">
            <div className="flex justify-between mb-1">
                <span className="text-sm text-gray-600">{label}</span>
                <span className="font-bold text-gray-900 text-sm">{fmt(value)}</span>
            </div>
            {total !== undefined && (
                <div className="flex items-center gap-2">
                    <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-[10px] text-gray-400 w-8 text-right">{pct}%</span>
                </div>
            )}
        </div>
    );
}

export default function LeadStats({
    totalLeads = 0, activeLeads = 0, leadsInFunnel = 0,
    leadsNewToday = 0, leadsInteractedToday = 0, leadsIn24h = 0,
    totalClicks = 0, clicksThisWeek = 0, clicksThisMonth = 0,
    clicksFunnel = 0, clicksBroadcast = 0,
    uniqueLeadsClicked = 0, topLeads = [],
    totalPages = 0, activePages = 0, leadsReached = 0,
    leadsWebhook = 0, leadsSync = 0,
    days = 7, onDaysChange, daily = [],
}: LeadStatsProps) {

    // KPI calculations
    const avg7 = daily.length > 0
        ? daily.slice(-7).reduce((s, d) => s + d.new_leads, 0) / Math.min(daily.length, 7)
        : 0;
    const engagementRate = activeLeads > 0 ? ((uniqueLeadsClicked / activeLeads) * 100).toFixed(1) : '0.0';
    const in24hPct = activeLeads > 0 ? Math.round((leadsIn24h / activeLeads) * 100) : 0;
    const activePct = totalLeads > 0 ? Math.round((activeLeads / totalLeads) * 100) : 0;
    const todayDelta = Math.round(leadsNewToday - avg7);
    const todayAboveAvg = leadsNewToday >= avg7;

    // Top leads
    const maxClicks = topLeads[0]?.total_clicks ?? 1;
    const topLeadsList = topLeads.slice(0, 8);

    return (
        <div className="space-y-4">
            {/* KPI row */}
            <div className="grid grid-cols-4 gap-3 mb-4">
                <KpiCard label="Leads ativos" value={fmt(activeLeads)} badge={`${activePct}% do total`} />
                <KpiCard label="Taxa de engajamento" value={`${engagementRate}%`} badge={`${fmt(uniqueLeadsClicked)} leads únicos`} />
                <KpiCard
                    label="Novos hoje"
                    value={fmt(leadsNewToday)}
                    badge={daily.length === 0
                        ? 'sem histórico'
                        : `${todayAboveAvg ? '+' : '−'}${Math.abs(todayDelta)} vs média 7d`}
                    badgeClass={daily.length === 0 ? 'text-gray-400' : todayAboveAvg ? 'text-emerald-500' : 'text-red-400'}
                />
                <KpiCard label="Janela 24h" value={fmt(leadsIn24h)} badge={`${in24hPct}% dos ativos — elegíveis p/ broadcast`} />
            </div>

            <div className="flex gap-4">
            {/* Left panel */}
            <div className="w-64 shrink-0 space-y-4">
                {/* Páginas */}
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Páginas</p>
                    <div className="mb-3">
                        <p className="text-xs text-gray-400">Total de páginas</p>
                        <p className="text-2xl font-bold text-gray-900">{totalPages}</p>
                    </div>
                    {[
                        { label: 'Ativas', value: activePages },
                    ].map(r => (
                        <div key={r.label} className="flex justify-between py-1.5 border-t border-gray-100 text-sm">
                            <span className="text-gray-500">{r.label}</span>
                            <span className="font-semibold text-gray-800">{r.value}</span>
                        </div>
                    ))}
                </div>

                {/* Indicadores */}
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Indicadores de leads</p>
                    <IndicatorRow label="Total de leads" value={totalLeads} />
                    <IndicatorRow label="Leads ativos" value={activeLeads} total={totalLeads} color="bg-emerald-500" />
                    <IndicatorRow label="Janela 24h" value={leadsIn24h} total={activeLeads} color="bg-orange-400" />
                    <IndicatorRow label="Alcançáveis" value={leadsReached} total={activeLeads} color="bg-emerald-600" />
                    <IndicatorRow label="Em funil" value={leadsInFunnel} total={activeLeads} color="bg-blue-500" />
                    <IndicatorRow label="Novos hoje" value={leadsNewToday} />
                    <IndicatorRow label="Interagiram hoje" value={leadsInteractedToday} />
                </div>
            </div>

            {/* Right content */}
            <div className="flex-1 min-w-0 space-y-4">
                {/* Metric cards */}
                <div className="grid grid-cols-3 gap-3">
                    {/* Card primário — hierarquia */}
                    <div className="bg-white border border-gray-200 rounded-xl p-4 col-span-1">
                        <div className="flex items-start justify-between mb-1">
                            <span className="text-xs text-gray-400">Cliques totais</span>
                            <MousePointerClick className="w-4 h-4 text-gray-300" />
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{fmt(totalClicks)}</p>
                        <p className="text-[11px] text-gray-400 mt-0.5">{fmt(uniqueLeadsClicked)} leads únicos</p>
                    </div>
                    <MetricCard label="Cliques na semana" value={fmt(clicksThisWeek)} icon={<TrendingUp className="w-4 h-4" />} />
                    <MetricCard label="Cliques no mês" value={fmt(clicksThisMonth)} icon={<CalendarDays className="w-4 h-4" />} />
                </div>

                {/* Origem dos cliques + leads */}
                <div className="grid grid-cols-2 gap-3">
                        {/* Cliques por origem */}
                        <div className="bg-white border border-gray-200 rounded-xl p-4">
                            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Cliques por origem</p>
                            {[
                                { label: 'Funil', value: clicksFunnel, color: 'bg-violet-500', pct: totalClicks > 0 ? Math.round(clicksFunnel / totalClicks * 100) : 0 },
                                { label: 'Broadcast', value: clicksBroadcast, color: 'bg-emerald-500', pct: totalClicks > 0 ? Math.round(clicksBroadcast / totalClicks * 100) : 0 },
                            ].map(r => (
                                <div key={r.label} className="flex items-center gap-2.5 py-2 border-t border-gray-100 first:border-0">
                                    <span className={`w-2 h-2 rounded-full ${r.color} shrink-0`} />
                                    <span className="text-sm text-gray-600 flex-1">{r.label}</span>
                                    <span className="text-xs text-gray-400">{r.pct}%</span>
                                    <span className="font-bold text-gray-900 text-sm">{fmt(r.value)}</span>
                                </div>
                            ))}
                        </div>
                        {/* Leads por origem */}
                        <div className="bg-white border border-gray-200 rounded-xl p-4">
                            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Leads por origem</p>
                            {[
                                { label: 'Webhook (interação real)', value: leadsWebhook, color: 'bg-blue-500', pct: totalLeads > 0 ? Math.round(leadsWebhook / totalLeads * 100) : 0 },
                                { label: 'Sync (varredura)', value: leadsSync, color: 'bg-orange-400', pct: totalLeads > 0 ? Math.round(leadsSync / totalLeads * 100) : 0 },
                            ].map(r => (
                                <div key={r.label} className="flex items-center gap-2.5 py-2 border-t border-gray-100 first:border-0">
                                    <span className={`w-2 h-2 rounded-full ${r.color} shrink-0`} />
                                    <span className="text-sm text-gray-600 flex-1">{r.label}</span>
                                    <span className="text-xs text-gray-400">{r.pct}%</span>
                                    <span className="font-bold text-gray-900 text-sm">{fmt(r.value)}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                {/* Top leads list */}
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                    <h3 className="text-base font-semibold text-gray-900 mb-0.5">Top leads por cliques</h3>
                    <p className="text-xs text-gray-400 mb-3">Leads mais ativos por volume de cliques</p>
                    {topLeadsList.length === 0 ? (
                        <p className="text-sm text-gray-400 py-8 text-center">Sem dados de cliques</p>
                    ) : (
                        <div>
                            {topLeadsList.map(lead => {
                                const initials = `${lead.nome?.[0] ?? ''}${(lead.sobrenome ?? '')[0] ?? ''}`.toUpperCase();
                                const avatarColor = AVATAR_COLORS[lead.id % AVATAR_COLORS.length];
                                const barWidth = Math.round((lead.total_clicks / maxClicks) * 100);
                                const pageShort = `...${lead.page_id.slice(-6)}`;
                                return (
                                    <div key={lead.id} className="flex items-center gap-3 py-2 border-t border-gray-100 first:border-0">
                                        <div className={`w-8 h-8 rounded-full ${avatarColor} flex items-center justify-center text-white text-xs font-bold shrink-0`}>
                                            {initials}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-gray-800 font-medium truncate">{lead.nome} {lead.sobrenome}</p>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                                                    <div className="h-full bg-blue-400 rounded-full" style={{ width: `${barWidth}%` }} />
                                                </div>
                                                <span className="text-[10px] text-gray-400 shrink-0">{pageShort}</span>
                                            </div>
                                        </div>
                                        <span className="font-bold text-gray-900 text-sm shrink-0">{fmt(lead.total_clicks)}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Evolução */}
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                    {/* Header com seletor de período */}
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h3 className="text-base font-semibold text-gray-900">Evolução</h3>
                            <p className="text-xs text-gray-400 mt-0.5">Novos leads, interações e cliques por dia</p>
                        </div>
                        <div className="flex items-center bg-gray-100 rounded-lg p-0.5 gap-0.5">
                            {([7, 30, 90] as DayRange[]).map(d => (
                                <button
                                    key={d}
                                    onClick={() => onDaysChange?.(d)}
                                    className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                                        days === d ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    {d}d
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Métricas do período */}
                    {daily.length > 0 && (() => {
                        const totalNew = daily.reduce((s, d) => s + d.new_leads, 0);
                        const totalWebhook = daily.reduce((s, d) => s + (d.new_leads_flow ?? 0), 0);
                        const totalSync = daily.reduce((s, d) => s + (d.new_leads_sync ?? 0), 0);
                        const totalInteracted = daily.reduce((s, d) => s + d.interacted, 0);
                        const totalClicks = daily.reduce((s, d) => s + d.clicks, 0);
                        const prevHalf = daily.slice(0, Math.floor(daily.length / 2));
                        const currHalf = daily.slice(Math.floor(daily.length / 2));
                        const pct = (curr: number, prev: number) =>
                            prev === 0 ? null : Math.round(((curr - prev) / prev) * 100);
                        const newPct = pct(
                            currHalf.reduce((s, d) => s + d.new_leads, 0),
                            prevHalf.reduce((s, d) => s + d.new_leads, 0)
                        );
                        const intPct = pct(
                            currHalf.reduce((s, d) => s + d.interacted, 0),
                            prevHalf.reduce((s, d) => s + d.interacted, 0)
                        );
                        return (
                            <>
                                <div className="grid grid-cols-3 gap-3 mb-3">
                                    {[
                                        { label: 'Novos leads', value: totalNew, pct: newPct, color: 'text-blue-600' },
                                        { label: 'Clicadores únicos', value: totalInteracted, pct: intPct, color: 'text-violet-600' },
                                        { label: 'Cliques', value: totalClicks, pct: null, color: 'text-emerald-600' },
                                    ].map(m => (
                                        <div key={m.label} className="bg-gray-50 rounded-lg p-3">
                                            <p className="text-xs text-gray-400">{m.label}</p>
                                            <p className={`text-xl font-bold mt-0.5 ${m.color}`}>{fmt(m.value)}</p>
                                            {m.pct !== null && (
                                                <p className={`text-[11px] mt-0.5 font-medium ${m.pct >= 0 ? 'text-emerald-500' : 'text-red-400'}`}>
                                                    {m.pct >= 0 ? '+' : ''}{m.pct}% vs período anterior
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                {(totalWebhook > 0 || totalSync > 0) && (
                                    <div className="flex items-center gap-4 mb-4 px-1">
                                        <span className="text-[11px] text-gray-400">Origem:</span>
                                        <span className="flex items-center gap-1.5 text-[11px] font-medium text-blue-600">
                                            <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
                                            Webhook {fmt(totalWebhook)}
                                            {totalNew > 0 && <span className="text-gray-400 font-normal">({Math.round(totalWebhook / totalNew * 100)}%)</span>}
                                        </span>
                                        <span className="flex items-center gap-1.5 text-[11px] font-medium text-orange-500">
                                            <span className="w-2 h-2 rounded-full bg-orange-400 inline-block" />
                                            Sync {fmt(totalSync)}
                                            {totalNew > 0 && <span className="text-gray-400 font-normal">({Math.round(totalSync / totalNew * 100)}%)</span>}
                                        </span>
                                    </div>
                                )}
                            </>
                        );
                    })()}

                    {/* Gráficos */}
                    {daily.length === 0 ? (
                        <p className="text-sm text-gray-400 py-8 text-center">Sem dados para o período</p>
                    ) : (
                        <>
                            {/* Gráfico A — Captação */}
                            <p className="text-xs font-semibold text-gray-400 mb-2">Captação</p>
                            <ResponsiveContainer width="100%" height={160}>
                                <LineChart data={daily} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={(v: string) => v.slice(5)} />
                                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                                    <RechartTooltip content={renderDailyTooltip} />
                                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                                    <Line type="monotone" dataKey="new_leads_flow" name="Flow" stroke="#3b82f6" strokeWidth={2} dot={false} />
                                    {daily.some(d => d.new_leads_sync > 0) && (
                                        <Line type="monotone" dataKey="new_leads_sync" name="Sync" stroke="#f97316" strokeWidth={2} dot={false} strokeDasharray="4 3" />
                                    )}
                                </LineChart>
                            </ResponsiveContainer>

                            {/* Gráfico B — Engajamento */}
                            <div className="border-t border-gray-100 mt-4 pt-4">
                                <p className="text-xs font-semibold text-gray-400 mb-2">Engajamento</p>
                                <ResponsiveContainer width="100%" height={160}>
                                    <LineChart data={daily} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                        <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={(v: string) => v.slice(5)} />
                                        <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                                        <RechartTooltip content={renderDailyTooltip} />
                                        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                                        <Line type="monotone" dataKey="interacted" name="Interações únicas" stroke="#8b5cf6" strokeWidth={2} dot={false} />
                                        <Line type="monotone" dataKey="clicks_funnel" name="Cliques funil" stroke="#10b981" strokeWidth={2} dot={false} />
                                        <Line type="monotone" dataKey="clicks_broadcast" name="Cliques broadcast" stroke="#06b6d4" strokeWidth={2} dot={false} strokeDasharray="4 3" />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </>
                    )}
                </div>
            </div>
            </div>
        </div>
    );
}
